/**
 * Helper functions for opening an image from the user's local file system and posting to Higharc's file store.
 * @module
 */
import { encodePath } from "common/utils/encodePath";
import { uploadFileNoRead } from "common/utils/uploadFile";
import { postFile } from "lib/storage/fileStorage";

const defaultImageTypes = ["png", "jpeg", "gif", "webp"] as const;

export async function uploadImage(
  options?: { sizeLimit?: number; imageTypes?: Partial<typeof defaultImageTypes> },
  onProgress?: (progress: number) => void,
  ...pathRoot: string[]
): Promise<{ fileSize: number; filename: string; url: string; width: number; height: number }> {
  const imageTypes = options?.imageTypes ?? defaultImageTypes;
  const file = await uploadFileNoRead(imageTypes.map((type) => `image/${type}`).join(","), options?.sizeLimit);

  onProgress?.(0);
  const [url, err] = await postFile(file, encodePath([...pathRoot, file.name]));
  if (err) {
    throw err;
  }

  if (!url) {
    throw new Error("No URL returned from file storage");
  }

  onProgress?.(0.5);
  const { src, width, height } = await loadImage(url);
  onProgress?.(1);

  return { url: src, width, height, filename: file.name, fileSize: file.size };
}

export async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    // get the image size
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      resolve(img);
    };

    img.onabort = () => {
      reject("aborted");
    };

    img.onerror = () => {
      reject("Could not load the image.");
    };

    img.src = src;
  });
}
